/**
 * Collaboration configuration.
 *
 * The `COLLABORATION_SERVICE_URL` can be overridden via the DotenvWebpack
 * plugin from the `.env` file or via environment variables. When not set,
 * the URL is derived from `window.location` so that it automatically
 * points to the Nginx `/collaboration/` proxy in production.
 *
 * Authentication is handled end-to-end via the browser's Django `sessionid`
 * cookie: nginx serves the collaboration service (`/collaboration/`) and
 * cloud-login (`/api/*`, `/auth/*`) on the same origin, so the cookie flows
 * automatically on the WebSocket upgrade. No client-side token handling.
 */

/**
 * Build the default production WebSocket URL from the current page origin.
 * In production, an Nginx reverse-proxy serves the collaboration service
 * at the `/collaboration/` path on the same host.
 */
const getDefaultCollaborationUrl = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  return `${protocol}//${window.location.host}/collaboration/`;
};

/**
 * The WebSocket URL for the Collaboration Service.
 * Uses the env var when set (development), otherwise derives from window.location (production).
 */
export const getCollaborationServiceUrl = (): string => {
  return process.env.COLLABORATION_SERVICE_URL || getDefaultCollaborationUrl();
};

/**
 * The HTTP(S) URL for the Collaboration Service REST endpoints (e.g. the
 * writer-reassignment route). Derived from the WebSocket URL so it respects
 * the dev env override and the production same-origin Nginx proxy:
 * `wss://…/collaboration/` → `https://…/collaboration/` (and `ws://` → `http://`).
 *
 * Always returns a trailing slash so callers can safely concatenate a path
 * (`${getCollaborationServiceHttpUrl()}${projectId}/writer`) even when
 * `COLLABORATION_SERVICE_URL` is set without one. Returns `''` unchanged in
 * non-browser contexts where no URL is available.
 */
export const getCollaborationServiceHttpUrl = (): string => {
  const url = getCollaborationServiceUrl();
  if (!url) {
    return '';
  }
  const httpUrl = url.replace(/^ws/, 'http');
  return httpUrl.endsWith('/') ? httpUrl : `${httpUrl}/`;
};

/**
 * Check whether the Collaboration Service URL is available.
 *
 * @returns true if a collaboration URL is available
 */
export const isCollaborationConfigured = (): boolean =>
  Boolean(getCollaborationServiceUrl());
